import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';

const ImagePreviewModal = ({ isOpen, imageSrc, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setImageLoaded(false);
      setZoomLevel(1);
      // App shell already locks body scroll — do not mutate body overflow
    }

    return undefined;
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300);
  }, [onClose]);

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);

  const handleZoomIn = useCallback(() => {
    setZoomLevel((prev) => Math.min(prev + 0.2, 3));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoomLevel((prev) => Math.max(prev - 0.2, 1));
  }, []);

  const handleResetZoom = useCallback(() => {
    setZoomLevel(1);
  }, []);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      data-rt-modal-overlay
      className={`fixed inset-0 flex items-center justify-center transition-opacity duration-300 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
      style={{
        zIndex: 20000,
        backgroundColor: isVisible ? 'rgba(0, 0, 0, 0.95)' : 'rgba(0, 0, 0, 0)',
        pointerEvents: isVisible ? 'auto' : 'none',
      }}
      onClick={handleClose}
    >
      {/* Close Button */}
      <button
        onClick={handleClose}
        className="absolute top-6 right-6 text-white hover:text-gray-300 transition-colors p-2 hover:bg-white hover:bg-opacity-10 rounded-lg backdrop-blur-sm z-10"
        aria-label="Close"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>

      {/* Toolbar */}
      <div
        className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex items-center gap-3 bg-black bg-opacity-60 backdrop-blur-md px-6 py-3 rounded-full"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Zoom Out Button */}
        {/* <button
          onClick={handleZoomOut}
          disabled={zoomLevel === 1}
          className="text-white hover:text-gray-300 disabled:text-gray-600 disabled:cursor-not-allowed transition-colors p-2 hover:bg-white hover:bg-opacity-10 rounded-lg"
          aria-label="Zoom out"
          title="Zoom out (-)​"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
            <line x1="8" y1="11" x2="14" y2="11" />
          </svg>
        </button> */}

        {/* Zoom Level Display */}
        <div className="text-white text-sm font-medium min-w-[50px] text-center">
          {Math.round(zoomLevel * 100)}%
        </div>

        {/* Zoom In Button */}
        {/* <button
          onClick={handleZoomIn}
          disabled={zoomLevel === 3}
          className="text-white hover:text-gray-300 disabled:text-gray-600 disabled:cursor-not-allowed transition-colors p-2 hover:bg-white hover:bg-opacity-10 rounded-lg"
          aria-label="Zoom in"
          title="Zoom in (+)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
            <line x1="11" y1="8" x2="11" y2="14" />
            <line x1="8" y1="11" x2="14" y2="11" />
          </svg>
        </button> */}

        {/* Reset Button */}
        {/* <button
          onClick={handleResetZoom}
          disabled={zoomLevel === 1}
          className="text-white hover:text-gray-300 disabled:text-gray-600 disabled:cursor-not-allowed transition-colors p-2 hover:bg-white hover:bg-opacity-10 rounded-lg"
          aria-label="Reset zoom"
          title="Reset zoom"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="21.5 2 21.5 8.5 15 8.5" />
            <polyline points="2.5 22 2.5 15.5 9 15.5" />
            <path d="M3.532 5.612A9 9 0 0 1 21.468 18.388" />
            <path d="M20.468 5.612A9 9 0 0 0 2.532 18.388" />
          </svg>
        </button> */}
      </div>

      {/* Image Container */}
      <div className="relative w-full h-full flex items-center justify-center overflow-hidden">
        {/* Loading State */}
        {!imageLoaded && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-12 h-12 border-4 border-gray-400 border-t-white rounded-full animate-spin" />
          </div>
        )}

        {/* Image */}
        <img
          src={imageSrc}
          alt="Preview"
          onLoad={handleImageLoad}
          onClick={(e) => e.stopPropagation()}
          className={`max-w-full max-h-full transition-all duration-300 ${
            imageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          style={{
            transform: `scale(${zoomLevel})`,
            cursor: 'grab',
          }}
          draggable="false"
        />
      </div>

      {/* Help Text */}
      <div
        className="absolute top-6 left-6 text-gray-300 text-sm backdrop-blur-sm bg-black bg-opacity-40 px-4 py-2 rounded-lg"
        onClick={(e) => e.stopPropagation()}
      >
        Press <kbd className="bg-gray-700 px-2 py-1 rounded ml-1">ESC</kbd> to close
      </div>
    </div>,
    document.body
  );
};

export default ImagePreviewModal;
